import { Classes, Dialog } from "@blueprintjs/core";
import { FGListen, FieldGroup, showError } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import {
  FcbRemplacementDefinitifDto,
  FcbRemplacementDefinitifDtoFromJSON,
  ReferentialApi,
  RemplacementDefinitiveApi
} from "../../../../../api";
import { FGWalterSelectInput, SmallFormGenerator } from "../../../../../components";
import { useApiService, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { IRemplacement } from "./RemplacementInterfaces";

const DialogStyled = styled(Dialog)`
  width: 900px;
  background: white;
`;

export interface IRemplacementDefinitiveDetailPageProps {
  isOpen: boolean;
  onSave: () => void;
  onCancel: () => void;
  data: IRemplacement;
}

export const RemplacementDefinitiveDetailPage: React.FunctionComponent<IRemplacementDefinitiveDetailPageProps> = ({
  isOpen,
  onSave,
  onCancel,
  data
}) => {
  const { t } = useTl();
  const [saving, setSaving] = React.useState(false);
  const value = React.useMemo(
    () =>
      FcbRemplacementDefinitifDtoFromJSON({
        idremplacement: data.idremplacement,
        idremplacant: data.idremplacant,
        idformateurLocalisationContrat: data.idformateurLocalisationContrat,
        idclasseMatiere: data.idclasseMatiere
      }),
    [data]
  );

  const [remplacant, setRemplacant] = React.useState(data.idremplacant);
  const idcentre = React.useMemo(() => data?.idcentre, [data?.idcentre]);

  const [formateur, formateurLoading] = useReferential(
    a => a.referentialGetFormateurByCentre({ idCentre: idcentre }),
    true,
    [idcentre]
  );
  const getContratFormateurFunc = React.useCallback(
    (a: ReferentialApi) =>
      remplacant > 0
        ? a.referentialGetFormateurLocalisationContrat({ idformateur: remplacant, idcentre: idcentre })
        : null,
    [idcentre, remplacant]
  );

  const [contratFormateur, cf] = useReferential(getContratFormateurFunc, true, [remplacant, idcentre]);

  const cfItems = React.useMemo(() => {
    if (remplacant > 0 && !!contratFormateur) {
      return contratFormateur;
    } else {
      return [];
    }
  }, [contratFormateur, remplacant]);

  const api = useApiService(RemplacementDefinitiveApi);
  const saveItem = React.useCallback(
    async (v: FcbRemplacementDefinitifDto) => {
      try {
        setSaving(true);
        await api.remplacementDefinitiveSave({ FcbRemplacementDefinitifDto: v });
        onSave();
      } catch (error) {
        showError(t(ETLCodes.Error));
      }
      setSaving(false);
    },
    [api, onSave, t]
  );

  // const remplacants = React.useMemo(() => formateur?.filter(f => f.value !== data.idremplacant), [
  //   formateur,
  //   data.idremplacant
  // ]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      idformateurLocalisationContrat: Yup.number()
        .nullable()
        .when("idremplacant", (value, schema) => (value ? schema.required(t(ETLCodes.Required)) : schema))
    });
  }, [t]);

  return (
    <DialogStyled isOpen={isOpen} enforceFocus={false}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={value}
          onSubmit={saveItem}
          saving={saving}
          onCancel={onCancel}
          editMode={true}
          validationSchema={FormSchema}
        >
          <FGListen
            field="idremplacant"
            onChanged={(val, formik) => {
              setRemplacant(val);
              if (!val || val !== remplacant) {
                formik.setFieldValue("idformateurLocalisationContrat", null);
              }
            }}
          />
          <FieldGroup>
            <FGWalterSelectInput
              name="idremplacant"
              label={t(ETLCodes.Remplacant)}
              items={formateur}
              loading={formateurLoading}
            />
          </FieldGroup>
          <FieldGroup>
            <FGWalterSelectInput
              name="idformateurLocalisationContrat"
              label={t(ETLCodes.Contrat)}
              items={cfItems}
              loading={cf}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </DialogStyled>
  );
};
